import React, { useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
  Autocomplete,
  Button,
  ClickAwayListener,
  Popper,
  PopperProps,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";
import { useInvestorsSelector } from "store";
import { RoutesKeys } from "store/types";

interface Props {
  investorId: number;
}

export const InvestorPicker: React.FC<Props> = ({ investorId }) => {
  const investors = useInvestorsSelector();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);

  const investor = useMemo(
    () => investors.find((investor) => investor.id === Number(investorId)),
    [investors, investorId]
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectInvestor = (newInvestorId: number) => {
    // Detect current tab using matchPath
    const isCorrelationTab = matchPath(pathname, Routes.portfolioConstructionInvestorCorrelation);

    // Default to allocation route, change to correlation if needed
    let route: RoutesKeys = "portfolioConstructionInvestorAllocation";

    if (isCorrelationTab) {
      route = "portfolioConstructionInvestorCorrelation";
    }

    navigate({
      route,
      params: { investor_id: newInvestorId }
    });
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-start" gap="5px">
      <MaterialTooltip isShow placement="top" title={investor?.name}>
        <Button
          onClick={handleClick}
          sx={{
            width: "160px",
            padding: "4px 8px",
            backgroundColor: "white",
            border: "1px solid #7b92a9",
            borderRadius: "32px",
            minHeight: 0,
            textTransform: "none",
            "&:hover": { backgroundColor: "#f6f8fc" }
          }}
          endIcon={open ? <ArrowDropUpIcon sx={{ fontSize: 16 }} /> : <ArrowDropDownIcon sx={{ fontSize: 16 }} />}
        >
          <Typography
            sx={{
              maxWidth: "240px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
              color: "#071f35"
            }}
          >
            {investor?.name || "Loading..."}
          </Typography>
        </Button>
      </MaterialTooltip>
      <Popper placement="bottom-start" sx={{ zIndex: 5 }} open={open} anchorEl={anchorEl}>
        <ClickAwayListener onClickAway={handleClose}>
          <Autocomplete
            disablePortal
            options={investors}
            open
            popupIcon={""}
            onChange={(_, option) => option && selectInvestor(option.id)}
            getOptionLabel={(option) => option.name}
            renderOption={(props, { id, name }) => {
              return (
                <li
                  {...props}
                  key={id}
                  style={{ fontSize: "14px", cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#edf1f9")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                >
                  {name}
                </li>
              );
            }}
            PopperComponent={CustomPopper}
            sx={{
              border: "none",
              width: 250,
              background: "white",
              boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25);",
              borderRadius: "10px 10px 0 0"
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search an investor..."
                sx={{ "& fieldset": { border: "none" } }}
                autoFocus
              />
            )}
          />
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
};

const CustomPopper = (props: PopperProps) => {
  const { children, ...other } = props;

  return (
    <Popper
      {...other}
      sx={{
        borderTop: "2px solid #bac7ce",
        minWidth: 250,
        boxShadow: "4px 10px 10px -7px rgba(0, 0, 0, 0.25)",
        borderRadius: "0 0 10px 10px",
        zIndex: 5
      }}
    >
      {children}
    </Popper>
  );
};
